import { Link } from "react-router-dom"
import { Sparkles, ArrowUpRight, ArrowLeft } from "lucide-react"
import { Button } from "@edemics/ui/button"
import { Card, CardContent } from "@edemics/ui/card"

interface ModulePremiumUpsellPageProps {
  /**
   * Human-readable module label (e.g. "IEP"). Falls back to the raw
   * key when the caller doesn't know a nicer name.
   */
  moduleName?: string
  /** Optional list of required module keys — surfaced for support triage. */
  requiredKeys?: string[]
  /**
   * Where the "Upgrade to Premium" CTA sends the user. Defaults to
   * the tenant-app subscription route (`/subscription/*` in App.tsx).
   */
  upgradePath?: string
}

/**
 * H4 (2026-09-03) — rendered when a tenant reaches a URL for a
 * premium-gated module (SAModule.isPremium=true) that their plan
 * tier doesn't cover and no super-admin override enables. Distinct
 * from ModuleUnavailablePage: this one leads with an upgrade CTA
 * rather than "contact your admin", because the module IS available
 * — it just costs more.
 *
 * The upsell copy is deliberately restrained (one sentence + one
 * CTA) so it reads as informative rather than pushy. If we ever want
 * a richer upgrade grid, put it on `/subscription`; this page's job
 * is only to point deep-links there.
 */
export function ModulePremiumUpsellPage({
  moduleName,
  requiredKeys,
  upgradePath = "/subscription",
}: ModulePremiumUpsellPageProps) {
  const displayName = moduleName ?? (requiredKeys ?? [])[0] ?? "This module"

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-8">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
          <div className="rounded-full bg-violet-100 p-3 text-violet-600">
            <Sparkles className="h-8 w-8" />
          </div>

          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-foreground">
              {displayName} is a Premium feature
            </h1>
            <p className="text-sm text-muted-foreground">
              Upgrade your subscription to Premium to unlock {displayName} and
              every other premium module on the platform.
            </p>
            {requiredKeys && requiredKeys.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                <span className="font-medium">Module key:</span>{" "}
                <code className="rounded bg-muted px-1.5 py-0.5">
                  {requiredKeys.join(", ")}
                </code>
              </p>
            )}
          </div>

          <div className="mt-2 flex gap-2">
            <Link to={upgradePath}>
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white">
                <ArrowUpRight className="mr-2 h-4 w-4" />
                Upgrade to Premium
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
